import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useState, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
import { Eye, EyeOff, Lock } from 'lucide-react';
const calculateStrength = (password, rules) => {
    const checks = {};
    let score = 0;
    const totalRules = Object.keys(rules || {}).length || 5;
    // Default rules
    const { minLength = 8, requireUppercase = true, requireLowercase = true, requireNumber = true, requireSpecial = true, } = rules || {};
    // Check minimum length
    checks.minLength = password.length >= minLength;
    if (checks.minLength)
        score++;
    // Check uppercase
    if (requireUppercase) {
        checks.uppercase = /[A-Z]/.test(password);
        if (checks.uppercase)
            score++;
    }
    // Check lowercase
    if (requireLowercase) {
        checks.lowercase = /[a-z]/.test(password);
        if (checks.lowercase)
            score++;
    }
    // Check number
    if (requireNumber) {
        checks.number = /\d/.test(password);
        if (checks.number)
            score++;
    }
    // Check special character
    if (requireSpecial) {
        checks.special = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
        if (checks.special)
            score++;
    }
    // Calculate level
    const percentage = score / totalRules;
    let level = 'weak';
    if (percentage >= 1)
        level = 'strong';
    else if (percentage >= 0.75)
        level = 'good';
    else if (percentage >= 0.5)
        level = 'fair';
    return { level, score, checks };
};
export const PasswordField = forwardRef(({ label, error, helperText, size = 'md', required, fullWidth = true, showLockIcon = true, allowToggle = true, showStrength = false, strengthRules, className, wrapperClassName, labelClassName, disabled, id, value, onChange, ...props }, ref) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    const inputId = id || React.useId();
    const [showPassword, setShowPassword] = useState(false);
    const [internalValue, setInternalValue] = useState('');
    const currentValue = value !== undefined ? String(value) : internalValue;
    const handleChange = useCallback((e) => {
        setInternalValue(e.target.value);
        onChange?.(e);
    }, [onChange]);
    const toggleVisibility = useCallback(() => {
        setShowPassword(prev => !prev);
    }, []);
    const strength = showStrength ? calculateStrength(currentValue, strengthRules) : null;
    // Size classes
    const sizeClasses = {
        sm: isCompact ? 'h-7 text-xs px-2' : 'h-8 text-sm px-2.5',
        md: isCompact ? 'h-8 text-sm px-2.5' : 'h-10 text-sm px-3',
        lg: isCompact ? 'h-10 text-base px-3' : 'h-12 text-base px-4',
    };
    const labelSizeClasses = {
        sm: isCompact ? 'text-xs' : 'text-xs',
        md: isCompact ? 'text-xs' : 'text-sm',
        lg: isCompact ? 'text-sm' : 'text-base',
    };
    const iconSizeClasses = {
        sm: 'h-3.5 w-3.5',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
    };
    const strengthColors = {
        weak: 'bg-red-500',
        fair: 'bg-yellow-500',
        good: 'bg-blue-500',
        strong: 'bg-green-500',
    };
    const strengthLabels = {
        weak: 'Weak',
        fair: 'Fair',
        good: 'Good',
        strong: 'Strong',
    };
    const strengthWidths = {
        weak: 'w-1/4',
        fair: 'w-1/2',
        good: 'w-3/4',
        strong: 'w-full',
    };
    return (_jsxs("div", { className: cn('space-y-1.5', fullWidth && 'w-full', wrapperClassName), children: [label && (_jsxs("label", { htmlFor: inputId, className: cn('block font-medium text-gray-700 dark:text-gray-200', labelSizeClasses[size], labelClassName), children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsxs("div", { className: "relative", children: [showLockIcon && (_jsx("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500", children: _jsx(Lock, { className: iconSizeClasses[size] }) })), _jsx("input", { ref: ref, id: inputId, type: showPassword ? 'text' : 'password', disabled: disabled, required: required, value: value, onChange: handleChange, className: cn(
                        // Base styles
                        'w-full rounded-md border bg-white dark:bg-gray-800 transition-all duration-200', 'placeholder:text-gray-400 dark:placeholder:text-gray-500', 'text-gray-900 dark:text-white', 
                        // Focus styles
                        'focus:outline-none focus:ring-2 focus:ring-offset-0', 
                        // Size
                        sizeClasses[size], 
                        // Adornments padding
                        showLockIcon && 'pl-10', allowToggle && 'pr-10', 
                        // States
                        error
                            ? 'border-red-500 dark:border-red-400 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500'
                            : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400', 
                        // Disabled
                        disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700', 
                        // Hover
                        !disabled && !error && 'hover:border-gray-400 dark:hover:border-gray-500', className), ...props }), allowToggle && (_jsx("button", { type: "button", onClick: toggleVisibility, disabled: disabled, tabIndex: -1, className: cn('absolute inset-y-0 right-0 pr-3 flex items-center', 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300', 'transition-colors', disabled && 'cursor-not-allowed'), children: showPassword ? (_jsx(EyeOff, { className: iconSizeClasses[size] })) : (_jsx(Eye, { className: iconSizeClasses[size] })) }))] }), showStrength && currentValue && strength && (_jsxs("div", { className: "space-y-1", children: [_jsx("div", { className: "h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden", children: _jsx("div", { className: cn('h-full transition-all duration-300 rounded-full', strengthWidths[strength.level], strengthColors[strength.level]) }) }), _jsxs("p", { className: cn(isCompact ? 'text-xs' : 'text-sm', strength.level === 'weak' && 'text-red-600 dark:text-red-400', strength.level === 'fair' && 'text-yellow-600 dark:text-yellow-400', strength.level === 'good' && 'text-blue-600 dark:text-blue-400', strength.level === 'strong' && 'text-green-600 dark:text-green-400'), children: ["Password strength: ", strengthLabels[strength.level]] })] })), error && (_jsx("p", { className: cn('text-red-600 dark:text-red-400', isCompact ? 'text-xs' : 'text-sm'), children: error })), helperText && !error && (_jsx("p", { className: cn('text-gray-500 dark:text-gray-400', isCompact ? 'text-xs' : 'text-sm'), children: helperText }))] }));
});
PasswordField.displayName = 'PasswordField';
